<template>
    <div v-if="open" class="cv3-modal-overlay" @click.self="close">
        <div class="cv3-vmodal">
            <header class="cv3-vmodal-head">
                <div class="cv3-vmodal-title">
                    <UserPlusIcon :size="18" />
                    <h2 class="!ppv2-text-white">{{ $t('checkout.v3.new_client') }}</h2>
                </div>
                <button type="button" class="cv3-modal-close" @click="close"><XIcon :size="20" /></button>
            </header>

            <div class="cv3-vmodal-body">
                <!-- Type de client -->
                <p class="cv3-vlabel">{{ $t('checkout.v3.client_type_label') }}</p>
                <div class="cv3-vradios">
                    <label :class="{ active: form.customer_type === '0' }">
                        <input type="radio" value="0" v-model="form.customer_type" /> {{ $t('checkout.v3.client_individual') }}
                    </label>
                    <label :class="{ active: form.customer_type === '1' }">
                        <input type="radio" value="1" v-model="form.customer_type" /> {{ $t('checkout.v3.client_professional') }}
                    </label>
                </div>

                <!-- Professionnel : raison sociale / SIRET / TVA / fonction -->
                <template v-if="form.customer_type === '1'">
                    <div class="cv3-vgrid">
                        <input v-model="form.social_reason" :placeholder="$t('checkout.v3.social_reason_placeholder')" />
                        <input v-model="form.fonction" :placeholder="$t('checkout.v3.fonction_placeholder')" />
                        <input v-model="form.siret" :placeholder="$t('checkout.v3.siret_placeholder')" />
                        <input v-model="form.tva" :placeholder="$t('checkout.v3.tva_placeholder')" />
                    </div>
                </template>

                <!-- Civilité -->
                <p class="cv3-vlabel">{{ $t('checkout.v3.gender_label') }}</p>
                <div class="cv3-vradios">
                    <label v-for="g in genders" :key="g.value" :class="{ active: form.gender === g.value }">
                        <input type="radio" :value="g.value" v-model="form.gender" /> {{ g.label }}
                    </label>
                </div>

                <!-- Identité -->
                <p class="cv3-vlabel">{{ $t('checkout.v3.identity_label') }}</p>
                <div class="cv3-vgrid">
                    <div class="cv3-vfield">
                        <input
                            v-model.trim="form.first_name"
                            :class="{ invalid: errors.first_name }"
                            :placeholder="$t('checkout.v3.first_name_placeholder') + ' *'"
                        />
                        <span v-if="errors.first_name" class="cv3-verror">{{ errors.first_name[0] }}</span>
                    </div>
                    <div class="cv3-vfield">
                        <input
                            v-model.trim="form.last_name"
                            :class="{ invalid: errors.last_name }"
                            :placeholder="$t('checkout.v3.last_name_placeholder') + ' *'"
                        />
                        <span v-if="errors.last_name" class="cv3-verror">{{ errors.last_name[0] }}</span>
                    </div>
                    <div class="cv3-vfield">
                        <input
                            v-model.trim="form.email"
                            type="email"
                            :class="{ invalid: errors.email }"
                            :placeholder="$t('checkout.v3.email_placeholder') + ' *'"
                        />
                        <span v-if="errors.email" class="cv3-verror">{{ errors.email[0] }}</span>
                    </div>
                    <div class="cv3-vfield">
                        <input v-model.trim="form.phone" :placeholder="$t('checkout.v3.phone_placeholder')" />
                    </div>
                </div>

                <p class="cv3-vhint">{{ $t('checkout.v3.required_fields_hint') }}</p>
            </div>

            <footer class="cv3-vmodal-foot">
                <button type="button" class="cv3-acompte-btn" @click="close">{{ $t('checkout.v3.cancel_btn') }}</button>
                <button type="button" class="cv3-validate-pay" :disabled="submitting || !isValid" @click="submit">
                    <CheckIcon :size="18" /> {{ $t('checkout.v3.add_btn') }}
                </button>
            </footer>
        </div>
    </div>
</template>

<script>
import { UserPlus as UserPlusIcon, X as XIcon, Check as CheckIcon } from 'lucide-vue'

export default {
    name: 'AddClientModal',
    components: { UserPlusIcon, XIcon, CheckIcon },
    props: {
        store: { type: Object, required: true },
        open: { type: Boolean, default: false },
    },
    data() {
        return {
            form: this.blank(),
            errors: {},
            submitting: false,
        }
    },
    computed: {
        genders() {
            return [
                { value: 'Homme', label: this.$t('checkout.v3.gender_male') },
                { value: 'Femme', label: this.$t('checkout.v3.gender_female') },
                { value: 'Autre', label: this.$t('checkout.v3.gender_other') },
            ]
        },
        isValid() {
            return !!(this.form.first_name && this.form.last_name && this.form.email && this.form.email.includes('@'))
        },
    },
    watch: {
        open(v) {
            if (v) {
                this.form = this.blank()
                this.errors = {}
            }
        },
    },
    methods: {
        blank() {
            return {
                customer_type: '0',
                gender: null,
                first_name: '',
                last_name: '',
                email: '',
                phone: '',
                social_reason: '',
                fonction: '',
                siret: '',
                tva: '',
            }
        },
        close() {
            this.$emit('close')
        },
        payload() {
            const f = this.form
            const pro = f.customer_type === '1'
            return {
                customer_type: f.customer_type,
                gender: f.gender || '',
                first_name: f.first_name,
                last_name: f.last_name,
                email: f.email,
                phone: f.phone,
                social_reason: pro ? f.social_reason : '',
                fonction: pro ? f.fonction : '',
                siret: pro ? f.siret : '',
                tva: pro ? f.tva : '',
            }
        },
        async submit() {
            if (this.submitting || !this.isValid) return
            this.errors = {}
            this.submitting = true
            try {
                const res = await this.store.createCustomer(this.payload())
                if (!res.ok) {
                    if (res.errors) this.errors = res.errors
                    this.$toast && this.$toast.error(this.$t('checkout.v3.error_creating_client'))
                    return
                }
                await this.store.selectCustomer({
                    id: res.userId,
                    first_name: this.form.first_name,
                    last_name: this.form.last_name,
                    email: this.form.email,
                    is_guest: false,
                })
                this.$toast && this.$toast.success(this.$t('checkout.v3.client_created'))
                this.$emit('created', res.userId)
                this.close()
            } catch (e) {
                this.$toast && this.$toast.error(this.$t('checkout.v3.error_creating_client'))
            } finally {
                this.submitting = false
            }
        },
    },
}
</script>

<style scoped lang="scss">
.cv3-modal-overlay {
    position: fixed;
    inset: 0;
    z-index: 1100;
    background: rgba(3, 6, 20, 0.7);
    backdrop-filter: blur(3px);
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 24px;
}
.cv3-vmodal {
    width: 100%;
    max-width: 560px;
    max-height: 92vh;
    background: var(--cv3-panel);
    border: 1px solid var(--cv3-border-input);
    border-radius: 20px;
    display: flex;
    flex-direction: column;
    overflow: hidden;
    font-family: 'Poppins', sans-serif;
    color: var(--cv3-text);
}
.cv3-vmodal-head {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 18px 22px;
    border-bottom: 1px solid var(--cv3-border);
}
.cv3-vmodal-title {
    display: flex;
    align-items: center;
    gap: 10px;
    h2 {
        margin: 0;
        font-size: 17px;
        font-weight: 700;
    }
}
.cv3-modal-close {
    border: 0;
    background: transparent;
    color: var(--cv3-muted);
    display: inline-flex;
}
.cv3-vmodal-body {
    padding: 18px 22px;
    overflow-y: auto;
    display: flex;
    flex-direction: column;
    gap: 10px;
}
.cv3-vlabel {
    margin: 6px 0 0;
    font-size: 11px;
    font-weight: 700;
    letter-spacing: 0.5px;
    text-transform: uppercase;
    color: var(--cv3-muted);
}
.cv3-vhint {
    margin: 4px 0 0;
    font-size: 12px;
    color: var(--cv3-text-2);
}
.cv3-vradios {
    display: flex;
    gap: 10px;
    flex-wrap: wrap;
    label {
        flex: 1 1 0;
        display: flex;
        align-items: center;
        gap: 8px;
        padding: 10px 12px;
        border-radius: 10px;
        border: 1px solid var(--cv3-border-input);
        font-size: 13px;
        cursor: pointer;
        &.active {
            border-color: var(--cv3-accent);
            background: var(--cv3-icon-tint);
        }
    }
}
.cv3-vgrid {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 8px;
}
.cv3-vfield {
    display: flex;
    flex-direction: column;
    gap: 3px;
}
.cv3-vmodal-body input[type='text'],
.cv3-vmodal-body input:not([type]),
.cv3-vmodal-body input[type='email'] {
    background: var(--cv3-input);
    border: 1px solid var(--cv3-border-input);
    border-radius: 9px;
    color: var(--cv3-text);
    padding: 9px 11px;
    font-size: 13px;
    width: 100%;
    &::placeholder {
        color: var(--cv3-muted);
    }
    &.invalid {
        border-color: #f87171;
    }
}
.cv3-verror {
    font-size: 11px;
    color: #f87171;
}
.cv3-vmodal-foot {
    padding: 16px 22px;
    border-top: 1px solid var(--cv3-border);
    display: flex;
    gap: 10px;
}
.cv3-acompte-btn {
    flex: 0 0 auto;
    padding: 11px 18px;
    border-radius: 11px;
    border: 1px solid var(--cv3-border-input);
    background: transparent;
    color: var(--cv3-text-2);
    font-weight: 600;
    font-size: 14px;
}
.cv3-validate-pay {
    flex: 1 1 auto;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    gap: 8px;
    padding: 11px 18px;
    border-radius: 11px;
    border: 0;
    background: var(--cv3-accent);
    color: #fff;
    font-weight: 700;
    font-size: 14px;
}
</style>
